import {
  IonInput,
  IonLabel,
  IonList,
  IonRouterContext,
  IonText,
} from '@ionic/react';
import { Form, Formik, useField } from 'formik';
import { cloneDeep, set } from 'lodash';
import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { usePermissions } from '../../api/authorization.provider';
import {
  GetMachineDocument,
  ListMachinesDocument,
  Machine,
  MachineConnectionType,
  UpdateMachineInput,
  useDisableMachineMutation,
  useGetTeamQuery,
  useUpdateMachineMutation,
} from '../../api/graphql/generated';
import {
  trackMachineChangedTeam,
  trackMachineDeactivated,
} from '../../config/analytics';
import { IntercomArticle } from '../../config/intercom';
import {
  createDirectConnectDeactivateTask,
  createDirectConnectMachineFromCloudMachine,
  useDirectConnectSaveTasksAndNavigateToMachine,
} from '../../direct-connect/helper/direct-connect-helper';
import { getMachineDetailsPageId } from '../../pages/machine/machine-details.page';
import { useBeaconRouter } from '../../route/route.hooks';
import { runInBackground, sleep } from '../../util/async';
import { logger } from '../../util/logger';
import { useQueryParameters } from '../../util/router';
import { useConfirmActionAlert } from '../alert/confirmation-alert';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { BaseButton } from '../button/base-button';
import { DeleteButton } from '../button/delete-button';
import { LoadingButton } from '../button/loading-button';
import { WithConfirmation } from '../button/with-confirmation';
import { IonFormField } from '../form/ion-form.field';
import { IntercomHelp } from '../help/intercom-help';
import { IonItem } from '../ionic/IonItem';
import { TeamExplorer } from '../team/team-explorer';
import { SaveStatusToast } from '../toast/toast.gql';
import { FieldLocked } from '../ui/field-locked';
import { RegisterMachineModal } from './registration/register-machine-modal';

interface MachineDetailsProps {
  machine?: Machine;
}

const MachineDetails = (props: MachineDetailsProps): ReactElement => {
  const { machine } = props;

  const { i18n } = useTranslation();
  const machineSchema = yup.object().shape({
    name: yup.string().required('*'),
  });

  const queryParams = useQueryParameters();
  const initializeWithReactivate =
    queryParams.get('reactivate') === 'true' && machine?.active === false
      ? true
      : false;

  const { push } = useContext(IonRouterContext);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);

  const reactivate = useCallback(async () => {
    if (initializeWithReactivate) {
      await sleep(750);
      setIsRegistering(true);
    }
  }, [initializeWithReactivate]);

  useEffect(() => {
    reactivate();
  }, [reactivate]);

  const [callUpdateMachineApi, { loading, error }] = useUpdateMachineMutation({
    refetchQueries: [ListMachinesDocument],
  });

  const { builder } = useBeaconRouter();

  const [disableMachine, { loading: loadingDisableMachine }] =
    useDisableMachineMutation({
      variables: { input: { id: machine?.id as string } },
      refetchQueries: [
        ListMachinesDocument,
        {
          query: GetMachineDocument,
          variables: { input: { id: machine?.id as string } },
        },
      ],
    });

  const {
    delete: deleteMachine,
    update,
    create: createMachine,
  } = usePermissions('machine', machine);

  const onSubmit = async (form: Partial<Machine>) => {
    const teamDidChange = machine?.teamId !== form.teamId;
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { __typename, state, ...input } = form;
    await updateMachine(input);
    if (teamDidChange) {
      trackMachineChangedTeam();
      if (machine?.teamId) {
        push(builder.getMachinesRoute(machine?.teamId), 'back');
      }
    }
  };

  const updateMachine = async (input: UpdateMachineInput) => {
    try {
      await callUpdateMachineApi({
        variables: {
          input,
        },
      });
      logger.info('Successfully updated machine');
    } catch (error) {
      logger.error('Error updating machine', { error, input });
    }
  };

  const saveTasksAndNavigateToDirectConnectMachine =
    useDirectConnectSaveTasksAndNavigateToMachine();

  const isConnectedToCloud = machine?.state?.online;

  const inactivateMachine = useCallback(async () => {
    logger.info('disabling machine', { id: machine?.id });

    const serialNumber = machine?.state?.runtimeInfo?.serialNumber;
    const success = await disableMachine();

    if (success) {
      trackMachineDeactivated();

      // if we are online then we don't need to perform a deactivate handoff
      // since the device will receive the cloud message to deprovision
      if (
        machine?.connectionType === MachineConnectionType.DIRECT &&
        !isConnectedToCloud
      ) {
        const directConnectMachine =
          createDirectConnectMachineFromCloudMachine(machine);

        const deactivateTask = createDirectConnectDeactivateTask(serialNumber, {
          id: directConnectMachine.id,
        });

        saveTasksAndNavigateToDirectConnectMachine(directConnectMachine, [
          deactivateTask,
        ]);

        return;
      }

      if (machine?.teamId) {
        push(builder.getMachinesRoute(machine?.teamId), 'back');
      }
    }
  }, [
    builder,
    disableMachine,
    machine,
    push,
    saveTasksAndNavigateToDirectConnectMachine,
    isConnectedToCloud,
  ]);

  const confirmationAlert = useConfirmActionAlert();

  const handleDelete = useMemo(() => {
    if (
      machine?.connectionType === MachineConnectionType.DIRECT &&
      !isConnectedToCloud
    ) {
      return confirmationAlert({
        confirmationAction: inactivateMachine,
        header: i18n.t('common:direct_connect_deactivating_alert_header'),
        message: i18n.t('common:direct_connect_deactivating_alert_message'),
        confirmText: i18n.t('common:im_near'),
        cancelText: i18n.t('common:try_later'),
      });
    } else {
      return inactivateMachine;
    }
  }, [
    confirmationAlert,
    i18n,
    inactivateMachine,
    machine?.connectionType,
    isConnectedToCloud,
  ]);

  const isReadOnly = !update;

  const canDeactivate = deleteMachine && machine?.active === true;
  const canReactivate = createMachine && machine?.active !== true;

  const initialValues = useMemo<Machine>(() => {
    // The form requires field values to be strings so we
    // must translate non-string properties else they won't appear.
    const record = cloneDeep(machine ?? {}) as Machine;
    const label3PE = record.state?.runtimeInfo?.threePositionEnabled
      ? i18n.t('common:yes')
      : i18n.t('common:no');
    set(record, 'state.runtimeInfo.threePositionEnabled', label3PE);
    return record;
  }, [machine, i18n]);

  const registerMachineModalOnDidDismiss = useCallback(
    (registeredMachine?: Machine) => {
      runInBackground(async () => {
        setIsRegistering(false);
        if (registeredMachine?.active) {
          await sleep(500);
          push(
            builder.getMachineRoute(
              registeredMachine?.teamId,
              registeredMachine?.id
            ),
            'back'
          );
        }
      });
    },
    [builder, push]
  );

  return (
    <>
      <Formik
        validationSchema={machineSchema}
        onSubmit={onSubmit}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnMount={true}
      >
        {({ isValid, dirty, submitForm, initialValues, values }) => {
          return (
            <Form>
              <IonList lines="full">
                <MachineTeamSelectorField readonly={isReadOnly} />
                <IonFormField
                  label={i18n.t('common:machine_name')}
                  name="name"
                  inputProps={{
                    disabled: isReadOnly,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_serial_number')}
                  name="state.runtimeInfo.serialNumber"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_mfg')}
                  name="state.runtimeInfo.manufacturer"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_model')}
                  name="state.runtimeInfo.model"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_polyscope')}
                  name="state.runtimeInfo.softwareVersion"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_firmware')}
                  name="state.runtimeInfo.firmwareVersion"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_urcap')}
                  name="state.runtimeInfo.urcapVersion"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_teach_pendant_3pe')}
                  name="state.runtimeInfo.threePositionEnabled"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_client_id')}
                  name="clientId"
                  inputProps={{
                    disabled: true,
                  }}
                />
                <IonFormField
                  label={i18n.t('common:machine_id')}
                  name="id"
                  inputProps={{
                    disabled: true,
                  }}
                />
              </IonList>
              <br />
              <RightAlignedButtons>
                {canDeactivate && (
                  <DeleteButton
                    header={i18n.t(
                      'common:machine_deactivate_robot_confirmation'
                    )}
                    onDelete={handleDelete}
                    buttonProps={{
                      type: 'button',
                      disabled: loadingDisableMachine,
                    }}
                  >
                    {i18n.t('common:machine_deactivate_robot')}
                  </DeleteButton>
                )}
                {canReactivate && (
                  <>
                    <BaseButton
                      buttonProps={{
                        type: 'button',
                        fill: 'outline',
                        onClick: () => {
                          setIsRegistering(true);
                        },
                      }}
                    >
                      {i18n.t('common:machine_reactivate_robot')}
                    </BaseButton>
                  </>
                )}
                {!isReadOnly && (
                  <WithConfirmation
                    header={i18n.t('common:machine_move_team_confirmation')}
                  >
                    {({ onConfirmed }) => {
                      return (
                        <LoadingButton
                          loading={loading}
                          buttonProps={{
                            disabled: !isValid || !dirty,
                            onClick: (e) => {
                              if (values.teamId !== initialValues.teamId) {
                                onConfirmed({ onConfirmation: submitForm });
                              } else {
                                e.stopPropagation();
                                submitForm();
                              }
                            },
                          }}
                        >
                          {i18n.t('common:update')}
                        </LoadingButton>
                      );
                    }}
                  </WithConfirmation>
                )}
              </RightAlignedButtons>
            </Form>
          );
        }}
      </Formik>
      <RegisterMachineModal
        isOpen={isRegistering}
        teamId={machine?.teamId as string}
        machine={machine}
        onDidDismiss={registerMachineModalOnDidDismiss}
      />
      <div className="ion-padding-top ion-margin-top content-center">
        <IonText className="ion-text-center" color="medium">
          <IntercomHelp
            articleId={IntercomArticle.MANAGING_ROBOTS}
            description={i18n.t('common:machine_learn_about_managing_machines')}
          />
        </IonText>
      </div>
      <SaveStatusToast loading={loading} error={error} />
    </>
  );
};

interface MachineTeamSelectorFieldProps {
  readonly?: boolean;
}

const MachineTeamSelectorField = (
  props: MachineTeamSelectorFieldProps
): ReactElement => {
  const { readonly } = props;
  const { i18n } = useTranslation();
  const [field, _meta, helper] = useField({ name: 'teamId' });

  const [isTeamExplorerOpen, setTeamExplorerOpen] = useState<boolean>(false);

  const { data } = useGetTeamQuery({
    variables: { input: { id: field.value } },
  });

  const teamName = data?.getTeam.name;

  const ref = document.getElementById(getMachineDetailsPageId());

  return (
    <>
      <IonItem
        onClick={
          readonly
            ? undefined
            : () => {
                setTeamExplorerOpen(true);
              }
        }
      >
        <IonInput
          aria-label="team-picker"
          labelPlacement="floating"
          value={teamName}
          readonly={true}
          disabled={readonly}
        >
          <div slot="label">
            <IonLabel color="primary">
              {i18n.t('common:team')} <FieldLocked show={readonly ?? false} />
            </IonLabel>
          </div>
        </IonInput>
      </IonItem>
      <TeamExplorer
        isOpen={isTeamExplorerOpen}
        presentingElement={ref}
        onClose={() => {
          setTeamExplorerOpen(false);
        }}
        onSelected={(teamId) => {
          helper.setValue(teamId);
        }}
      />
    </>
  );
};

export { MachineDetails };
